import styled from 'styled-components';
import {useToast} from "@chakra-ui/react";
import {useSelector} from "react-redux";

import React, {useState} from "react";
import {Bookmark, Ellipsis, MessageCircle, Pencil, Share, Shield} from "lucide-react";
import {useLoggedIn, useStoreProfile, useUsername} from '../store';
import {usePostVote} from "../api/post";
import useFeedback from "../utils/use_feedback";
import {selectPostVoteState} from "../store/selectors";
import useModal from "../modals";
import {themecolors} from "../utils/themecolors";
import {PostVote} from "./vote";
import {BareButton} from './button';
import CondensedPost from "./condensed_post";
import Sheet from "./bottom-sheet/sheet";
import PostManageSheet from "./bottom-sheet/post_manage";
import ModerateContentSheet from "./bottom-sheet/moderate_content";
import SaveSheet from "./bottom-sheet/save";
import {useSheetModal} from "./test/ModalContext";
import SignupPromptSheet from "./bottom-sheet/signup_prompt";
import PostActionsSheet from "./bottom-sheet/post_actions";

export const CommentCount = styled.div``;
const ActionText = styled.span``;
const CondensedActionText = styled.span``;

export const CondensedActionIcon = styled(({className, children, onClick, inPost, style}) => {
    return (
        <div className={className} style={style}>
            {onClick ?
                <BareButton onClick={onClick}>
                    {children}
                </BareButton> :
                <div>
                    {children}
                </div>
            }
        </div>
    );
})`
  display: flex;
  border-radius: 48px;
  align-items: center;
  height: 28px;
  min-width: 28px;
  font-size: 12px;
  margin: 8px 0;
  padding: 16px 12px;
  justify-content: center;
  color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_low_impact_text};
  background-color: ${({theme, inPost}) => inPost ? (theme.color.name === 'dark' ? themecolors.dark_background_3 : '#eaeaea') : 'none'};
  ${BareButton} {
    padding: 0;
    display: flex;
    font-weight: 600;
  }
  div {
    display: flex;
    align-items: center;
    font-weight: 600;
  }
  &:active {
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_high_impact_text : themecolors.light_high_impact_text};
  }
`;

export const ActionIcon = styled(({className, children, onClick, inPost, style}) => {
    return (
        <div className={className} style={style}>
            {onClick ?
                <BareButton onClick={onClick}>
                    {children}
                </BareButton> :
                <div>
                    {children}
                </div>
            }
        </div>
    );
})`
  display: flex;
  border-radius: 48px;
  align-items: center;
  height: 28px;
  min-width: 28px;
  font-size: 13px;
  margin: 8px 0;
  padding: 16px 10px;
  justify-content: center;
  background-color: ${({theme, inPost}) => inPost ? (theme.color.name === 'dark' ? themecolors.dark_background_3 : 'rgb(236 238 240)') : 'none'};
  border: 0px solid ${({theme, inPost}) => inPost ? (theme.color.name === 'dark' ? themecolors.dark_background_3 : themecolors.lighter_border) : 'none'};

  -webkit-tap-highlight-color: transparent;
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);
  tap-highlight-color: rgba(0, 0, 0, 0);
  
  ${BareButton} {
    padding: 0;
    display: flex;
    font-weight: 500;
  }
  div {
    display: flex;
    align-items: center;
    font-weight: 500;
  }

  &:active {
    background-color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_background_5 : 'rgb(221 224 227)'};
  }
  
`;

const PostActions = styled(({className, post, imageView, inPost, isTrending}) => {
    const {expand} = useStoreProfile();
    const filled = expand === 'card';
    const { openModal } = useSheetModal();
    const loggedIn = useLoggedIn();
    const {feedbackCh} = useFeedback();
    const vote = usePostVote(feedbackCh);
    const voteValue = useSelector(selectPostVoteState(post?.id));
    const username = useUsername();
    const toast = useToast();
    const handleVote = async (dir) => {
        if (!loggedIn) {

            const modalContent = ({ handleCloseModal }) => (
                <Sheet onClose={() => handleCloseModal() } padding={"header"}><SignupPromptSheet onClose={() => handleCloseModal() } /></Sheet>
            );

            openModal(modalContent);

        } else {
            try {
                await vote(post, dir);
            } catch {}
        }
    };
    const handleShare = () => {
        if (navigator.canShare && post) {
            navigator.share({
                title: post.title,
                url: `https://scrd.app/${post.uuid}/`,
            })
        } else if (post) {
            navigator.clipboard.writeText(`https://scrd.app/${post.uuid}/`);
            toast({
                title: `Outdated Browser.`,
                description: 'Link copied to clipboard.',
                status: 'error',
                isClosable: true,
            });
        }
    };

    const [saveOpen, setSaveOpen] = useState(false);
    const handleSave = () => {
        if (!loggedIn) {

            const modalContent = ({ handleCloseModal }) => (
                <Sheet onClose={() => handleCloseModal() } padding={"header"}><SignupPromptSheet onClose={() => handleCloseModal() } /></Sheet>
            );

            openModal(modalContent);

        } else {
            setSaveOpen(true);
        }
    };

    const [moderateContentOpen, setModerateContentOpen] = useState(false);
    const handleModeration = () => {
        setModerateContentOpen(true);
    };

    const [manageOpen, setManageOpen] = useState(false);

    const handleManage = () => {
        setManageOpen(true);
    };

    const [infoOpen, setInfoOpen] = useState(false);

    const handleInfo = () => {
        setInfoOpen(true);
    };

    const inPostOrFilled = inPost || filled;

    return (
        <div className={className} style={{paddingBottom: inPostOrFilled ? '0' : '2px'}}>
            <PostVote onVote={handleVote} vote={voteValue} score={post?.score} condensed={!inPostOrFilled}/>
            {inPostOrFilled ?
                <ActionIcon inPost={inPostOrFilled}>
                    <MessageCircle size={15}/>
                    <ActionText>{post?.comments || '0'}</ActionText>
                </ActionIcon>
                :
                <CondensedActionIcon style={{marginLeft: '6px'}}>
                    <CondensedActionText>{post?.comments || '0'} comments</CondensedActionText>
                </CondensedActionIcon>
            }

                {post?.moderation &&
                    <>
                        {inPostOrFilled ?
                            <ActionIcon onClick={handleModeration} inPost={inPostOrFilled}>
                                <Shield size={15}/>
                            </ActionIcon>
                        :
                            <CondensedActionIcon onClick={handleModeration}>
                                <CondensedActionText>Mod</CondensedActionText>
                            </CondensedActionIcon>
                        }
                    </>

                }
                {post && username === post?.author &&
                    <>
                    {inPostOrFilled ?
                            <ActionIcon onClick={handleManage} inPost={inPostOrFilled}>
                                <Pencil size={15}/>
                            </ActionIcon>
                            :
                        <>
                        {!post?.moderation &&
                            <CondensedActionIcon onClick={handleManage}>
                                <CondensedActionText>Edit</CondensedActionText>
                            </CondensedActionIcon>
                        }
                        </>
                    }
                    </>
                }

            {inPostOrFilled &&
                <ActionIcon onClick={handleSave} inPost={inPostOrFilled}>
                    <Bookmark size={15}/>
                </ActionIcon>
            }
            {inPostOrFilled ?
                <ActionIcon onClick={handleShare} inPost={inPostOrFilled}>
                    <Share size={15}/>
                </ActionIcon>
                :
                <>
                    {!post?.moderation &&
                        <CondensedActionIcon onClick={handleShare} inPost={inPostOrFilled}><CondensedActionText>Share</CondensedActionText></CondensedActionIcon>
                    }
                </>
            }
            {!inPostOrFilled &&
                <CondensedActionIcon onClick={handleInfo} inPost={inPostOrFilled}>
                    <Ellipsis size={18}/>
                </CondensedActionIcon>
            }
            {manageOpen && <Sheet onClose={() => setManageOpen(false) } padding={"menu"}><PostManageSheet postId={post?.id} onClose={() => setManageOpen(false) } /></Sheet>}
            {infoOpen && <Sheet onClose={() => setInfoOpen(false) } padding={"menu"}><PostActionsSheet save isTrending={isTrending} postId={post?.id} onClose={() => setInfoOpen(false) } /></Sheet>}
            {moderateContentOpen && <Sheet onClose={() => setModerateContentOpen(false) } padding={"menu"}><ModerateContentSheet post={post} onClose={() => setModerateContentOpen(false) } /></Sheet>}
            {saveOpen && <Sheet onClose={() => setSaveOpen(false) } padding={"header"}><SaveSheet post={post} onClose={() => setSaveOpen(false) } /></Sheet>}
        </div>
    );
})`
  display: flex;
  align-items: center;
  overflow: scroll;
  -ms-overflow-style: none;
  scrollbar-width: none;
  color: ${({theme, imageView}) => imageView ? themecolors.dark_medium_impact_text : theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_high_impact_text};
  fill: ${({theme, imageView}) => imageView ? themecolors.dark_medium_impact_text : theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_high_impact_text};
  padding: 0 16px;

  &::-webkit-scrollbar {
    display: none;
  }
  
  ${CondensedPost} & {
    font-size: 14px;
  }
  
  svg {
    height: 17px;
    width: 17px;
    align-items: center;
  }
  ${ActionIcon} {
    margin-left: 4px;
    flex-shrink: 0;
  }
  
  ${ActionIcon} button {
    align-items: center;
  }
  ${ActionText} {
    margin-left: 4px;
    color: ${({theme}) => theme.color.name === 'dark' ? themecolors.dark_medium_impact_text : themecolors.light_high_impact_text};
  }
  ${CondensedActionText} {
    white-space: nowrap;
  }
`;

export default PostActions;
